import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { ApiSurveyDto, ApiSurveyModelService, FieldworkRuleOverrideModel } from '../../../../shared/services/api-surveymodel.service';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';

interface DelayRow {
  segment: string;
  dateLabel: string;
  days: number;
}

interface ScheduleRuleRow {
  id: number;
  channels: string[];
  smsProvider: string;
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

@Component({
  selector: 'app-survey-fieldworks',
  templateUrl: './survey-fieldworks.component.html',
  styleUrls: ['./survey-fieldworks.component.css'],
  imports: [BreadcrumbComponent, PageLoaderComponent, SurveyHeaderComponent, InfoTooltipComponent]
})
export class SurveyFieldworksComponent implements OnInit {
  survey: ApiSurveyDto | null = null;
  loading = signal(false);
  error = signal<string | null>(null);

  private organizationId = '';
  private surveyId = '';

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private surveyModelService: ApiSurveyModelService) {}

  ngOnInit() {
    // surveySelectedGuard on the route guarantees these are present and valid.
    this.organizationId = this.organizationStore.selectedOrganizationId()!;
    this.surveyId = this.route.snapshot.paramMap.get('id')!;
    this.loadSurveyModel();
  }

  private loadSurveyModel(): void {
    this.loading.set(true);
    this.error.set(null);

    this.surveyModelService.getSurveyModel(this.organizationId, this.surveyId).subscribe({
      next: (survey) => {
        this.survey = survey;
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load fieldwork data. | ' + err);
        this.loading.set(false);
      }
    });
  }

  get delayRows(): DelayRow[] {
    const delay = this.survey?.fieldwork?.delay ?? {};
    return Object.entries(delay)
      .map(([segment, d]) => ({ segment, dateLabel: this.formatWords(d.date), days: d.days }))
      .sort((a, b) => {
        // "Root" is the default/fallback rule (applies to all segments not otherwise listed) —
        // always show it first, regardless of alphabetical order.
        if (a.segment === 'Root') return -1;
        if (b.segment === 'Root') return 1;
        return a.segment.localeCompare(b.segment);
      });
  }

  get weekDaysCode(): string {
    return this.survey?.fieldwork?.schedule?.weekDays ?? '';
  }

  get weekDayNames(): string[] {
    return this.parseWeekDays(this.weekDaysCode);
  }

  get scheduleRules(): ScheduleRuleRow[] {
    const rules = this.survey?.fieldwork?.schedule?.rules ?? {};
    return Object.entries(rules)
      .map(([id, r]) => ({ id: Number(id), channels: r.channels ?? [], smsProvider: r.smsProvider }))
      .sort((a, b) => a.id - b.id);
  }

  get ruleOverrides(): FieldworkRuleOverrideModel[] {
    return this.survey?.fieldwork?.schedule?.ruleOverride ?? [];
  }

  formatWords(value: string): string {
    return value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  formatChannel(channel: string): string {
    return channel === 'sms' ? 'SMS' : channel.charAt(0).toUpperCase() + channel.slice(1);
  }

  private parseWeekDays(code: string): string[] {
    const days = new Set<number>();

    for (const part of code.split(',').map(s => s.trim()).filter(Boolean)) {
      const rangeMatch = part.match(/^(\d)\s*-\s*(\d)$/);
      if (rangeMatch) {
        const start = Number(rangeMatch[1]);
        const end = Number(rangeMatch[2]);
        for (let d = start; d <= end; d++) days.add(d);
      } else if (/^\d$/.test(part)) {
        days.add(Number(part));
      }
    }

    return Array.from(days).sort((a, b) => a - b).map(d => WEEKDAY_NAMES[d - 1] ?? `Day ${d}`);
  }
}
